import { avatarColors, initialsOf } from '@/design-system/lib/avatar';
import { formatNumericDate } from '@escala/domain/dates';
import { paletteOf } from '@escala/domain/palette';
import {
  type ShareDocument,
  type ShareEvent,
  type SharePerson,
  type ShareRow,
  type ShareSection,
  dateBadge,
} from '@escala/domain/share';

import { HEADER_ICON_COLOR, iconKey } from './assets';
import { COLORS, type Ctx, ellipsize, font, headerColor, mix, roundRect, wrapLines } from './canvas';

/**
 * Layout da escala exportada, comum à imagem e ao PDF: o cabeçalho (organização,
 * título, período e o recorte), um card por evento — selo da data, título, horário e a
 * situação das vagas; cada ministério com as funções e as pessoas em "chips" com avatar
 * — e o rodapé. Tudo em unidades lógicas sobre uma largura fixa (`WIDTH`).
 *
 * Cada card é uma lista de peças (cabeçalho do evento, cabeçalho do ministério, linha da
 * função), medidas antes de desenhar: a imagem empilha os cards inteiros e o PDF os
 * reparte entre as páginas, quebrando entre as peças quando um card não cabe.
 */

export const WIDTH = 1080;
export const MARGIN = 48;
export const CARD_GAP = 24;
const CARD_PAD = 32;
const CARD_RADIUS = 28;

const COLUMN_GAP = 40;

/**
 * Tamanhos das peças. A imagem é lida no celular, com zoom: confortável, uma coluna. O
 * PDF vai para a impressão: compacto, dois ministérios por linha, para caber mais
 * eventos por página.
 */
export interface Density {
  chip: number;
  avatar: number;
  /** Corpo do nome no chip. */
  name: number;
  /** Corpo do nome da função. */
  label: number;
  labelW: number;
  chipGap: number;
  rowGap: number;
  section: number;
  sectionFont: number;
  sectionGap: number;
  columns: 1 | 2;
}

export const COMFORTABLE: Density = {
  chip: 48,
  avatar: 36,
  name: 20,
  label: 21,
  labelW: 200,
  chipGap: 10,
  rowGap: 14,
  section: 40,
  sectionFont: 18,
  sectionGap: 28,
  columns: 1,
};

export const COMPACT: Density = {
  chip: 36,
  avatar: 26,
  name: 16,
  label: 16,
  labelW: 124,
  chipGap: 8,
  rowGap: 8,
  section: 32,
  sectionFont: 14,
  sectionGap: 22,
  columns: 2,
};

export interface RenderContext {
  /** Nome da organização, no alto do cabeçalho. */
  org: string | null;
  color: string;
  /** Data da geração (`yyyy-mm-dd`), no rodapé. */
  generatedAt: string;
  /** Fotos já carregadas, por URL. */
  photos: ReadonlyMap<string, CanvasImageSource>;
  /** Ícones dos ministérios já carregados, por `iconKey` (ícone e cor). */
  icons: ReadonlyMap<string, CanvasImageSource>;
}

export type PieceKind = 'event' | 'continued' | 'section' | 'row';

export interface Piece {
  kind: PieceKind;
  height: number;
  /** Espaço acima da peça quando ela não abre o card. */
  gap: number;
  draw: (ctx: Ctx, x: number, y: number, width: number) => void;
}

export interface Card {
  event: ShareEvent;
  pieces: Piece[];
  /** O cabeçalho que abre o pedaço do card que continua em outra página. */
  continued: Piece;
}

/** Altura do card com estas peças (as margens internas incluídas). */
export function cardHeight(pieces: readonly Piece[]): number {
  return CARD_PAD * 2 + pieces.reduce((sum, p, i) => sum + p.height + (i ? p.gap : 0), 0);
}

export function drawCard(ctx: Ctx, pieces: readonly Piece[], x: number, y: number, width: number): void {
  const height = cardHeight(pieces);

  ctx.save();
  ctx.shadowColor = COLORS.shadow;
  ctx.shadowBlur = 24;
  ctx.shadowOffsetY = 6;
  roundRect(ctx, x, y, width, height, CARD_RADIUS);
  ctx.fillStyle = COLORS.card;
  ctx.fill();
  ctx.restore();
  roundRect(ctx, x + 0.5, y + 0.5, width - 1, height - 1, CARD_RADIUS);
  ctx.strokeStyle = COLORS.cardLine;
  ctx.lineWidth = 1;
  ctx.stroke();

  let cursor = y + CARD_PAD;

  pieces.forEach((piece, i) => {
    if (i) cursor += piece.gap;

    piece.draw(ctx, x + CARD_PAD, cursor, width - CARD_PAD * 2);
    cursor += piece.height;
  });
}

// ─── Peças do card ──────────────────────────────────────────────────────────────

function statusPill(ctx: Ctx, vacancies: number, right: number, top: number): void {
  const label = vacancies ? `${vacancies} ${vacancies > 1 ? 'vagas' : 'vaga'} em aberto` : 'Escala completa';
  const color = vacancies ? COLORS.warn : COLORS.ok;

  ctx.font = font(700, 18);
  const w = ctx.measureText(label).width + 52;
  const x = right - w;

  roundRect(ctx, x, top, w, 38, 19);
  ctx.fillStyle = vacancies ? COLORS.warnSoft : COLORS.okSoft;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = color;
  ctx.beginPath();

  if (vacancies) {
    // Círculo tracejado, como o ícone de "parcialmente preenchida" do app.
    ctx.setLineDash([3, 3]);
    ctx.arc(x + 22, top + 19, 7, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
  } else {
    ctx.moveTo(x + 16, top + 19);
    ctx.lineTo(x + 21, top + 24);
    ctx.lineTo(x + 29, top + 14);
    ctx.stroke();
  }

  ctx.fillStyle = color;
  ctx.fillText(label, x + 38, top + 26);
}

function eventVacancies(event: ShareEvent): number {
  return event.sections.reduce((s, x) => s + x.rows.reduce((r, row) => r + row.vacancies, 0), 0);
}

function eventPiece(event: ShareEvent): Piece {
  const badge = dateBadge(event.date);

  return {
    kind: 'event',
    height: 96,
    gap: 0,
    draw: (ctx, x, y, width) => {
      roundRect(ctx, x, y, 88, 96, 20);
      ctx.fillStyle = COLORS.primarySoft;
      ctx.fill();
      ctx.textAlign = 'center';
      ctx.fillStyle = COLORS.primary;
      ctx.font = font(800, 16);
      ctx.fillText(badge.weekday, x + 44, y + 26);
      ctx.fillStyle = COLORS.primaryDeep;
      ctx.font = font(800, 38);
      ctx.fillText(badge.day, x + 44, y + 64);
      ctx.fillStyle = COLORS.primary;
      ctx.font = font(700, 15);
      ctx.fillText(badge.month, x + 44, y + 85);
      ctx.textAlign = 'left';

      const pillRoom = 250;
      const textX = x + 112;

      ctx.fillStyle = COLORS.ink;
      ctx.font = font(800, 32);
      ctx.fillText(ellipsize(ctx, event.title, width - 112 - pillRoom), textX, y + 42);
      ctx.fillStyle = COLORS.muted;
      ctx.font = font(500, 22);
      ctx.fillText(ellipsize(ctx, event.when, width - 112 - pillRoom), textX, y + 78);

      statusPill(ctx, eventVacancies(event), x + width, y + 6);
    },
  };
}

function continuedPiece(event: ShareEvent): Piece {
  return {
    kind: 'continued',
    height: 34,
    gap: 0,
    draw: (ctx, x, y, width) => {
      ctx.font = font(800, 24);
      ctx.fillStyle = COLORS.ink;
      const title = ellipsize(ctx, event.title, width - 260);

      ctx.fillText(title, x, y + 26);
      const w = ctx.measureText(title).width;

      ctx.font = font(500, 20);
      ctx.fillStyle = COLORS.muted;
      ctx.fillText(` · ${event.when} · continuação`, x + w, y + 26);
    },
  };
}

/** Pílula do ministério, em frações da altura: a folga das pontas, o ícone e o espaço até o nome. */
const PILL_PAD = 0.3;
const PILL_ICON = 0.5;
const PILL_GAP = 0.28;

function sectionPiece(section: ShareSection, d: Density, rc: RenderContext): Piece {
  const tone = paletteOf(section.color);
  const h = d.section;

  return {
    kind: 'section',
    height: h,
    gap: d.sectionGap,
    draw: (ctx, x, y, width) => {
      ctx.font = font(800, d.sectionFont);
      const label = section.ministry.toUpperCase();
      // O ícone ocupa metade da altura, com respiro igual antes dele, até o nome e no fim.
      const iconX = x + h * PILL_PAD;
      const textX = iconX + h * (PILL_ICON + PILL_GAP);
      const w = Math.min(ctx.measureText(label).width, width - 120) + (textX - x) + h * PILL_PAD;

      roundRect(ctx, x, y, w, h, h * 0.3);
      ctx.fillStyle = tone.tint;
      ctx.fill();

      // O ícone do ministério na cor dele; sem o ícone carregado, a bolinha na cor.
      const icon = rc.icons.get(iconKey(section.icon, section.color));

      if (icon) {
        ctx.drawImage(icon, iconX, y + (h * (1 - PILL_ICON)) / 2, h * PILL_ICON, h * PILL_ICON);
      } else {
        ctx.fillStyle = section.color;
        ctx.beginPath();
        ctx.arc(iconX + (h * PILL_ICON) / 2, y + h / 2, h * 0.15, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = mix(section.color, '#000000', 0.15);
      ctx.fillText(
        ellipsize(ctx, label, x + w - h * PILL_PAD - textX),
        textX,
        y + h / 2 + d.sectionFont * 0.36
      );

      ctx.strokeStyle = COLORS.rule;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x + w + 12, y + h / 2);
      ctx.lineTo(x + width, y + h / 2);
      ctx.stroke();
    },
  };
}

type Chip = { width: number; person: SharePerson | null; label: string };

/** Respiro de cada lado do texto da vaga em aberto, em frações da altura do chip. */
const VACANCY_PAD = 0.38;

function drawAvatar(
  ctx: Ctx,
  person: SharePerson,
  x: number,
  y: number,
  size: number,
  rc: RenderContext
): void {
  const photo = person.photo ? rc.photos.get(person.photo) : undefined;
  const r = size / 2;

  ctx.save();
  ctx.beginPath();
  ctx.arc(x + r, y + r, r, 0, Math.PI * 2);
  ctx.closePath();

  if (photo) {
    ctx.clip();
    ctx.drawImage(photo, x, y, size, size);
  } else {
    const colors = avatarColors(person.name);

    ctx.fillStyle = colors.bg;
    ctx.fill();
    ctx.fillStyle = colors.fg;
    ctx.font = font(700, Math.round(size * 0.39));
    ctx.textAlign = 'center';
    ctx.fillText(initialsOf(person.name), x + r, y + r + size * 0.14);
  }

  ctx.restore();
}

function drawChip(ctx: Ctx, chip: Chip, x: number, y: number, d: Density, rc: RenderContext): void {
  const { person } = chip;
  const pad = (d.chip - d.avatar) / 2;
  const textX = x + pad + d.avatar + d.chipGap;
  const baseline = y + d.chip / 2 + d.name * 0.36;

  roundRect(ctx, x, y, chip.width, d.chip, d.chip / 2);

  if (!person) {
    // Vaga em aberto: discreta, abaixo de quem está escalado — só o contorno tracejado
    // claro e o texto apagado, sem avatar nem "+" (o arquivo não tem o que clicar).
    ctx.setLineDash([5, 4]);
    ctx.strokeStyle = COLORS.faint;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = COLORS.muted;
    ctx.font = font(500, d.name - 1);
    ctx.fillText(chip.label, x + d.chip * VACANCY_PAD, baseline);

    return;
  }

  ctx.fillStyle = person.highlight ? COLORS.primarySoft : COLORS.chip;
  ctx.fill();
  ctx.strokeStyle = person.highlight ? COLORS.primaryLine : COLORS.chipLine;
  ctx.lineWidth = person.highlight ? 2 : 1;
  ctx.stroke();
  drawAvatar(ctx, person, x + pad, y + pad, d.avatar, rc);
  ctx.fillStyle = person.highlight ? COLORS.primaryDeep : COLORS.ink;
  ctx.font = font(person.highlight ? 700 : 600, d.name);
  ctx.fillText(chip.label, textX, baseline);
}

function rowPiece(ctx: Ctx, row: ShareRow, width: number, d: Density, rc: RenderContext): Piece {
  const room = width - d.labelW;
  const pad = (d.chip - d.avatar) / 2;
  const chipOf = (person: SharePerson | null, label: string, weight: number): Chip => {
    ctx.font = font(weight, person ? d.name : d.name - 1);

    const text = ctx.measureText(label).width;

    return {
      person,
      label,
      width: Math.min(
        room,
        person ? pad + d.avatar + d.chipGap + text + d.chip * 0.4 : text + d.chip * VACANCY_PAD * 2
      ),
    };
  };
  const chips: Chip[] = [
    ...row.people.map((p) => chipOf(p, p.short, p.highlight ? 700 : 600)),
    ...(row.vacancies
      ? [chipOf(null, row.vacancies > 1 ? `${row.vacancies} vagas em aberto` : 'Vaga em aberto', 500)]
      : []),
  ];

  // Quebra os chips em linhas que cabem ao lado do rótulo.
  const lines: Chip[][] = [];

  for (const chip of chips) {
    const line = lines[lines.length - 1];
    const used = line ? line.reduce((s, c) => s + c.width + d.chipGap, 0) : 0;

    if (line && used + chip.width <= room) line.push(chip);
    else lines.push([chip]);
  }

  const count = Math.max(1, lines.length);

  return {
    kind: 'row',
    height: count * d.chip + (count - 1) * d.chipGap,
    gap: d.rowGap,
    draw: (c, x, y) => {
      c.fillStyle = COLORS.body;
      c.font = font(600, d.label);
      const label = wrapLines(c, row.role, d.labelW - 16, 2);
      const lineHeight = d.label * 1.25;
      const top = y + d.chip / 2 - (label.length * lineHeight) / 2;

      label.forEach((text, i) => c.fillText(text, x, top + i * lineHeight + d.label * 0.92));

      lines.forEach((line, i) => {
        let cx = x + d.labelW;

        for (const chip of line) {
          drawChip(c, chip, cx, y + i * (d.chip + d.chipGap), d, rc);
          cx += chip.width + d.chipGap;
        }
      });
    },
  };
}

/** As peças de um ministério: o cabeçalho e as funções. */
function sectionPieces(
  ctx: Ctx,
  section: ShareSection,
  width: number,
  d: Density,
  rc: RenderContext
): Piece[] {
  return [
    sectionPiece(section, d, rc),
    ...section.rows.map((row, i) => ({
      ...rowPiece(ctx, row, width, d, rc),
      gap: i ? d.rowGap : d.rowGap + 4,
    })),
  ];
}

/** Dois ministérios lado a lado, numa peça só (o PDF compacto). */
function bandPiece(
  ctx: Ctx,
  sections: readonly ShareSection[],
  width: number,
  d: Density,
  rc: RenderContext
): Piece {
  const colW = (width - COLUMN_GAP) / 2;
  const columns = sections.map((s) => sectionPieces(ctx, s, colW, d, rc));
  const heightOf = (pieces: readonly Piece[]) =>
    pieces.reduce((sum, p, i) => sum + p.height + (i ? p.gap : 0), 0);

  return {
    kind: 'row',
    height: Math.max(...columns.map(heightOf)),
    gap: d.sectionGap,
    draw: (c, x, y) => {
      columns.forEach((pieces, col) => {
        let cy = y;

        pieces.forEach((piece, i) => {
          if (i) cy += piece.gap;

          piece.draw(c, x + col * (colW + COLUMN_GAP), cy, colW);
          cy += piece.height;
        });
      });
    },
  };
}

/** Os cards dos eventos, já medidos para a largura do conteúdo. */
export function buildCards(
  ctx: Ctx,
  doc: ShareDocument,
  rc: RenderContext,
  d: Density = COMFORTABLE
): Card[] {
  const innerWidth = WIDTH - MARGIN * 2 - CARD_PAD * 2;

  return doc.events.map((event) => {
    const body =
      d.columns === 2
        ? event.sections.flatMap((_, i, all) =>
            i % 2 ? [] : [bandPiece(ctx, all.slice(i, i + 2), innerWidth, d, rc)]
          )
        : event.sections.flatMap((section) => sectionPieces(ctx, section, innerWidth, d, rc));

    return { event, continued: continuedPiece(event), pieces: [eventPiece(event), ...body] };
  });
}

// ─── Cabeçalho e rodapé ─────────────────────────────────────────────────────────

/** Faixa colorida do topo: organização, título, período e o recorte em pílulas. */
export function measureHeader(ctx: Ctx, doc: ShareDocument, rc: RenderContext): number {
  ctx.font = font(800, 46);
  const lines = wrapLines(ctx, doc.title, WIDTH - MARGIN * 2, 2).length;

  return 52 + (rc.org ? 38 : 0) + lines * 56 + 40 + (headerChips(doc, rc).length ? 68 : 0) + 44;
}

/** Os chips do cabeçalho: o ministério do recorte (com o ícone dele, em branco) e o membro. */
function headerChips(doc: ShareDocument, rc: RenderContext): { text: string; icon?: CanvasImageSource }[] {
  const ministry = doc.ministry
    ? [{ text: doc.ministry.name, icon: rc.icons.get(iconKey(doc.ministry.icon, HEADER_ICON_COLOR)) }]
    : [];

  return [...ministry, ...(doc.member ? [{ text: `Escala de ${doc.member}` }] : [])];
}

export function drawHeader(ctx: Ctx, doc: ShareDocument, rc: RenderContext): number {
  const height = measureHeader(ctx, doc, rc);
  const base = headerColor(rc.color);
  const gradient = ctx.createLinearGradient(0, 0, WIDTH, height);

  gradient.addColorStop(0, mix(base, '#000000', 0.05));
  gradient.addColorStop(1, mix(base, '#0F172A', 0.45));
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, WIDTH, height);

  // Dois círculos discretos no canto, como textura.
  ctx.fillStyle = 'rgba(255, 255, 255, 0.07)';
  ctx.beginPath();
  ctx.arc(WIDTH - 60, 30, 190, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(WIDTH - 250, height + 40, 120, 0, Math.PI * 2);
  ctx.fill();

  let y = 52;

  if (rc.org) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.78)';
    ctx.font = font(700, 20);
    ctx.fillText(ellipsize(ctx, rc.org.toUpperCase(), WIDTH - MARGIN * 2), MARGIN, y + 20);
    y += 38;
  }

  ctx.fillStyle = COLORS.white;
  ctx.font = font(800, 46);

  for (const line of wrapLines(ctx, doc.title, WIDTH - MARGIN * 2, 2)) {
    ctx.fillText(line, MARGIN, y + 44);
    y += 56;
  }

  ctx.fillStyle = 'rgba(255, 255, 255, 0.86)';
  ctx.font = font(500, 26);
  ctx.fillText(ellipsize(ctx, doc.subtitle, WIDTH - MARGIN * 2), MARGIN, y + 28);
  y += 40;

  const chips = headerChips(doc, rc);

  if (chips.length) {
    let x = MARGIN;

    y += 24;
    ctx.font = font(700, 20);

    for (const chip of chips) {
      const icon = chip.icon ? 34 : 0;
      const w = ctx.measureText(chip.text).width + 36 + icon;

      if (x + w > WIDTH - MARGIN) break;

      roundRect(ctx, x, y, w, 44, 22);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.16)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.28)';
      ctx.lineWidth = 1;
      ctx.stroke();
      if (chip.icon) ctx.drawImage(chip.icon, x + 16, y + 11, 22, 22);
      ctx.fillStyle = COLORS.white;
      ctx.fillText(chip.text, x + 18 + icon, y + 29);
      x += w + 10;
    }
  }

  return height;
}

/** Cabeçalho enxuto das páginas seguintes do PDF. */
export const COMPACT_HEADER = 76;

export function drawCompactHeader(ctx: Ctx, doc: ShareDocument, rc: RenderContext): void {
  ctx.fillStyle = headerColor(rc.color);
  ctx.fillRect(0, 0, WIDTH, 8);
  ctx.fillStyle = COLORS.ink;
  ctx.font = font(800, 22);
  const title = ellipsize(ctx, doc.title, WIDTH - MARGIN * 2 - 300);

  ctx.fillText(title, MARGIN, 52);
  ctx.fillStyle = COLORS.muted;
  ctx.font = font(500, 20);
  ctx.textAlign = 'right';
  ctx.fillText(ellipsize(ctx, doc.ministry?.name ?? '', 280), WIDTH - MARGIN, 52);
  ctx.textAlign = 'left';
}

export const FOOTER = 64;

export function drawFooter(
  ctx: Ctx,
  y: number,
  rc: RenderContext,
  page?: { index: number; total: number }
): void {
  ctx.fillStyle = COLORS.faint;
  ctx.font = font(500, 18);
  const text = `Gerado pelo Live Gospel em ${formatNumericDate(rc.generatedAt)}`;

  if (page) {
    ctx.fillText(text, MARGIN, y + 36);
    ctx.textAlign = 'right';
    ctx.fillText(`Página ${page.index} de ${page.total}`, WIDTH - MARGIN, y + 36);
  } else {
    ctx.textAlign = 'center';
    ctx.fillText(text, WIDTH / 2, y + 36);
  }

  ctx.textAlign = 'left';
}
